import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="holding-alerts"
export default class extends Controller {
  static targets = [ "criticalAlertsCell", "nonCriticalAlertsCell", "allClearCell" ]

  connect() {
    // Start processing alerts sequentially
    this.processAlertsSequentially();
  }

  async processAlertsSequentially() {
    // Fetch CSRF token once
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
    if (!csrfToken) {
      console.error('CSRF token not found. Cannot fetch alert counts.');
      this.showError('⚠️', 'CSRF token missing');
      return;
    }

    // Get all unique statement IDs from all target elements
    const statementIds = new Set();
    [...this.criticalAlertsCellTargets, ...this.nonCriticalAlertsCellTargets, ...this.allClearCellTargets].forEach(cell => {
      const statementId = cell.dataset.statementId;
      if (statementId) statementIds.add(statementId);
    });

    // Convert to array and reverse to process from bottom to top
    const statementIdsArray = Array.from(statementIds).reverse();

    for (const statementId of statementIdsArray) {
      // Find all cells for this statement ID
      const criticalCells = this.criticalAlertsCellTargets.filter(cell => cell.dataset.statementId === statementId);
      const nonCriticalCells = this.nonCriticalAlertsCellTargets.filter(cell => cell.dataset.statementId === statementId);
      const allClearCells = this.allClearCellTargets.filter(cell => cell.dataset.statementId === statementId);

      if (!statementId) {
        this.showErrorForCells([...criticalCells, ...nonCriticalCells], '❓', 'Missing statement ID');
        continue;
      }

      // Show spinner in critical cell only while loading
      criticalCells.forEach(cell => {
        cell.innerHTML = `<span class="spinner-border spinner-border-sm text-secondary" role="status" aria-hidden="true"></span>`;
      });
      nonCriticalCells.forEach(cell => {
        cell.innerHTML = '';
      });

      try {
        const response = await fetch(`/bank_account_statement_of_holdings/${statementId}/alerts_count`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken
          }
        });

        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch (e) {
            throw new Error(`HTTP error ${response.status}`);
          }
          throw new Error(errorData?.error || `HTTP error ${response.status}`);
        }

        const data = await response.json();
        this.updateAlertsDisplay(data, criticalCells, nonCriticalCells, allClearCells, statementId);

      } catch (error) {
        console.error(`Error fetching alert count for statement ID ${statementId}:`, error);
        this.showErrorForCells([...criticalCells, ...nonCriticalCells], '⚠️', error.message || 'Error loading count');
      }
    }
  }

  updateAlertsDisplay(data, criticalCells, nonCriticalCells, allClearCells, statementId) {
    // If statement is reviewed, always show green badge
    if (data.reviewed === true) {
      criticalCells.forEach(cell => {
        cell.innerHTML = '';
      });
      nonCriticalCells.forEach(cell => {
        cell.innerHTML = '';
      });
      allClearCells.forEach(cell => {
        cell.innerHTML = `
          <span class="badge rounded-pill bg-success" title="Reviewed" data-bs-toggle="tooltip">
            <i class="bi bi-check-circle-fill"></i> OK
          </span>`;
      });

      // Initialize Bootstrap tooltips
      allClearCells.forEach(cell => {
        const tooltipElements = cell.querySelectorAll('[data-bs-toggle="tooltip"]');
        tooltipElements.forEach(el => {
          if (typeof bootstrap !== 'undefined') {
            new bootstrap.Tooltip(el);
          }
        });
      });
      return;
    }

    // For backward compatibility, check if we have the new structured data or legacy count
    if (data.alerts_summary) {
      const criticalAlerts = data.alerts_summary.critical;
      const nonCriticalAlerts = data.alerts_summary.non_critical;

      // Update Critical Alerts
      criticalCells.forEach(cell => {
        if (criticalAlerts.count > 0) {
          const tooltip = `Critical: ${criticalAlerts.qty_delta} qty delta, ${criticalAlerts.book_cost_delta} book cost delta`;
          cell.innerHTML = `
            <span class="badge rounded-pill bg-danger" title="${tooltip}" data-bs-toggle="tooltip">
              ${criticalAlerts.count} <i class="bi bi-exclamation-triangle-fill"></i>
            </span>`;
        } else {
          cell.innerHTML = '';
        }
      });

      // Update Non-Critical Alerts
      nonCriticalCells.forEach(cell => {
        if (nonCriticalAlerts.count > 0) {
          const tooltip = `Non-critical: ${nonCriticalAlerts.market_value_delta} market value, ${nonCriticalAlerts.non_reconciled_entries} non-reconciled`;
          cell.innerHTML = `
            <span class="badge rounded-pill bg-warning text-dark" title="${tooltip}" data-bs-toggle="tooltip">
              ${nonCriticalAlerts.count} <i class="bi bi-info-circle-fill"></i>
            </span>`;
        } else {
          cell.innerHTML = '';
        }
      });

      // Show "All Clear" if no alerts
      allClearCells.forEach(cell => {
        if (data.alerts_summary.total_count === 0) {
          cell.innerHTML = `
            <span class="badge rounded-pill bg-success" title="All reconciled" data-bs-toggle="tooltip">
              <i class="bi bi-check-circle-fill"></i> OK
            </span>`;
        } else {
          cell.innerHTML = '';
        }
      });

    } else {
      // Fallback for legacy data structure
      const count = data.count || 0;
      criticalCells.forEach(cell => {
        const badgeClass = count > 0 ? 'bg-danger' : 'bg-success';
        const content = count > 0 ? `${count} <i class="bi bi-exclamation-triangle-fill"></i>` : '<i class="bi bi-check-circle-fill"></i> OK';
        cell.innerHTML = `<span class="badge ${badgeClass}">${content}</span>`;
      });

      nonCriticalCells.forEach(cell => {
        cell.innerHTML = '';
      });
    }

    // Initialize Bootstrap tooltips for new elements
    [...criticalCells, ...nonCriticalCells, ...allClearCells].forEach(cell => {
      const tooltipElements = cell.querySelectorAll('[data-bs-toggle="tooltip"]');
      tooltipElements.forEach(el => {
        if (typeof bootstrap !== 'undefined') {
          new bootstrap.Tooltip(el);
        }
      });
    });
  }

  showErrorForCells(cells, icon, message) {
    cells.forEach(cell => {
      cell.innerHTML = `<span title="${message}">${icon}</span>`;
    });
  }

  showError(icon, message) {
    [...this.criticalAlertsCellTargets, ...this.nonCriticalAlertsCellTargets].forEach(cell => {
      cell.innerHTML = `<span title="${message}">${icon}</span>`;
    });
  }
} 